/**
 * LocationViews — tabbed view switcher for the four "where is this bien"
 * representations we offer on the fiche:
 *
 *   • Cadastre   (SVG, IGN apicarto)        — ownership / parcel limits
 *   • Plan       (Google Maps roadmap)      — streets, POIs, quartier
 *   • Satellite  (IGN orthophoto WMS)       — built context + roof/garden
 *   • Vue rue    (Google Street View)       — pedestrian perspective
 *
 * All four share the same visual slot on the fiche so they never fight for
 * real estate. The user picks the view they care about.
 *
 * Tab state is local to this component; if we later want to persist the
 * last-used view across refreshes, lift it to a user preference.
 */

import { useState } from "react";
import { ParcelMap } from "./ParcelMap";
import { MapPlan } from "./MapPlan";
import { SatelliteMap } from "./SatelliteMap";
import { StreetView } from "./StreetView";
import type { CadastreParcel } from "../lib/types";

type Tab = "cadastre" | "plan" | "satellite" | "streetview";

const TABS: { key: Tab; label: string }[] = [
  { key: "cadastre", label: "Cadastre" },
  { key: "plan", label: "Plan" },
  { key: "satellite", label: "Satellite" },
  { key: "streetview", label: "Vue rue" },
];

export function LocationViews({
  parcel,
  lat,
  lng,
  label,
  className = "",
}: {
  parcel: CadastreParcel;
  lat: number;
  lng: number;
  /** Full address label from the geocode result — used by the Plan tab so
   *  Google shows a nicely-annotated pin instead of a raw coordinate. */
  label?: string | null;
  className?: string;
}) {
  const [tab, setTab] = useState<Tab>("cadastre");

  return (
    <div className={`relative ${className}`}>
      {/* Only the active view is mounted — keeping all four in the DOM
          would waste bandwidth on the Google iframes and the WMS tile. */}
      {tab === "cadastre" && (
        <ParcelMap parcel={parcel} className="h-full w-full" />
      )}
      {tab === "plan" && (
        <MapPlan lat={lat} lng={lng} label={label} className="h-full w-full" />
      )}
      {tab === "satellite" && (
        <SatelliteMap parcel={parcel} className="h-full w-full" />
      )}
      {tab === "streetview" && (
        <StreetView lat={lat} lng={lng} className="h-full w-full" />
      )}

      {/* Tab switcher — floats over the top-right corner on all three views.
          Solid dark pill matches the existing design token palette. */}
      <div className="absolute top-3 right-3 z-10 inline-flex rounded-full bg-ink-900/85 backdrop-blur-sm p-1 shadow-pop">
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`text-[11.5px] font-medium px-3 h-7 rounded-full transition ${
                active
                  ? "bg-lime-300 text-ink-900"
                  : "text-paper/85 hover:text-paper"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
